/**
 * app/App.tsx — Root application component
 * Updated: Added authentication gate
 */

import { useEffect, useState } from 'react';
import clsx                 from 'clsx';
import { Radar, Activity, Wrench } from 'lucide-react';
import { TopBar }           from '../components/dashboard/TopBar';
import { ScannerSidebar }   from '../components/scanner/ScannerSidebar';
import { AnalysisPanel }    from '../components/analysis/AnalysisPanel';
import { RightPanel }       from '../components/dashboard/RightPanel';
import { JournalAnalyticsView } from '../components/dashboard/JournalAnalyticsView';
import { AutoTraderPanel }  from '../components/autotrader/AutoTraderPanel';
import { LoginPage }        from '../components/auth/LoginPage';
import { useWebSocket }     from '../hooks/useWebSocket';
import { useAuth }          from '../hooks/useAuth';
import { useAccount, useUi, useStore } from '../store';
import { tradesApi }        from '../lib/api';

// Below lg there isn't room for scanner + analysis + tools side by side.
// md (tablet) keeps the scanner list beside one of the other two; below md
// (phone) shows exactly one pane, switched from a bottom nav.
type ScannerPane = 'list' | 'analysis' | 'tools';

const PANE_TABS: { id: ScannerPane; label: string; Icon: typeof Radar; cls: string }[] = [
  { id: 'list',     label: 'Scanner',  Icon: Radar,    cls: 'md:hidden' },
  { id: 'analysis', label: 'Analysis', Icon: Activity, cls: ''          },
  { id: 'tools',    label: 'Tools',    Icon: Wrench,   cls: ''          },
];

// ── Authenticated dashboard ───────────────────────────────────
function Dashboard({ onLogout }: { onLogout: () => void }) {
  const account = useAccount();
  const { mainTab } = useUi();
  const [pane, setPane] = useState<ScannerPane>('list');
  const selectedSym = useStore(s => s.sym);
  useWebSocket();

  // Picking a ticker in the list should take a phone straight to its analysis.
  useEffect(() => {
    if (selectedSym) setPane('analysis');
  }, [selectedSym]);

  // Load account stats on startup
  useEffect(() => {
    tradesApi.stats()
      .then(account.setStats)
      .catch(() => {
        account.setStats({
          balance: 2000, startingBalance: 2000,
          totalPnl: 0, totalPnlPct: 0,
          todayPnl: 0, weekPnl: 0, monthPnl: 0,
          winRate: 0, avgWin: 0, avgLoss: 0,
          avgRMultiple: 0, expectancy: 0,
          totalTrades: 0, openTrades: 0,
          consecutiveLosses: 0, maxRiskPerTrade: 40,
          phase: 1, updatedAt: new Date().toISOString(),
        });
      });
  }, [account.setStats]);

  return (
    <div className="flex flex-col h-app overflow-hidden">
      <TopBar onLogout={onLogout} />
      {mainTab === 'scanner' && (
        <>
          <div className="flex flex-1 overflow-hidden min-h-0">
            <div className={clsx(
              pane === 'list' ? 'flex' : 'hidden',
              'md:flex w-full md:w-72 md:flex-shrink-0 min-h-0'
            )}>
              <ScannerSidebar />
            </div>
            <main className={clsx(
              pane === 'analysis' ? 'flex' : 'hidden',
              pane !== 'tools' ? 'md:flex' : 'md:hidden',
              'lg:flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden'
            )}>
              <AnalysisPanel />
            </main>
            <div className={clsx(
              pane === 'tools' ? 'flex' : 'hidden',
              'lg:flex flex-1 lg:flex-none min-w-0 lg:w-72 lg:flex-shrink-0 min-h-0'
            )}>
              <RightPanel />
            </div>
          </div>

          <nav className="lg:hidden flex flex-shrink-0 border-t border-border bg-bg-1 pb-[env(safe-area-inset-bottom)]">
            {PANE_TABS.map(({ id, label, Icon, cls }) => (
              <button
                key={id}
                onClick={() => setPane(id)}
                className={clsx(
                  'flex-1 flex flex-col items-center gap-0.5 py-2 text-2xs font-mono transition-colors',
                  cls,
                  pane === id ? 'text-bull' : 'text-ink-3'
                )}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </nav>
        </>
      )}
      {mainTab === 'journal' && <JournalAnalyticsView />}
      {mainTab === 'autotrader' && (
        <div className="flex flex-1 overflow-hidden min-h-0">
          <AutoTraderPanel />
        </div>
      )}
    </div>
  );
}

// ── Loading screen (while checking existing session) ──────────
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-bg-0 flex items-center justify-center">
      <div className="text-center">
        <div className="font-display text-4xl font-extrabold tracking-tight mb-4">
          PHASE<span className="text-bull">1</span>
        </div>
        <div className="flex items-center justify-center gap-1.5">
          {[0, 0.15, 0.3].map((d, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-bull/60 animate-pulse"
              style={{ animationDelay: `${d}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Root app with auth gate ───────────────────────────────────
export function App() {
  const { auth, login, logout } = useAuth();

  // Still checking the existing session cookie
  if (auth.isChecking) {
    return <LoadingScreen />;
  }

  // Not logged in — show login page
  if (!auth.isAuthenticated) {
    return <LoginPage onLogin={login} />;
  }

  // Logged in — show the dashboard
  return <Dashboard onLogout={logout} />;
}
